<script lang="ts">
	import ContactCard from "@components/ContactCard.svelte";
	import HugeHeading from "@components/HugeHeading.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Link from "@components/common/Link.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { hugeHeadingParallax } from "@utils/hugeHeadingParallax.js";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { getCaseStudiesPath } from "@core/schema/paths/getCaseStudiesPath.js";
	import imgTeam from "../assets/images/about-us/team.jpg?webp&w=800&imagetools";
	import type { AboutUsProps } from "./AboutUsProps.js";

	const { contacts, tenant }: AboutUsProps = $props();
</script>

<Layout ogImage={imgTeam}>
	<section class="full-width pt-24 lg:pt-36 md:pt-28 md:mt-2 lg:mt-8 xl:mt-16 mb-8 md:mb-16 lg:mb-20">
		<div class="mx-auto w-full max-w-[87.5rem]">
			<h1
				class="sticky top-20 md:top-96 xl:top-[28rem] 2xl:top-[32rem] -translate-y-1/2 text-center text-nowrap uppercase font-druk-cond tracking-[0.01em] text-[8rem] md:text-[17rem] lg:text-[22rem] 2xl:text-[32rem]"
			>
				<Formatted text="stroke[[Náš příběh]]" />
			</h1>

			<div class="relative">
				<div class="sticky top-24 md:top-28 z-20">
					<div
						class="bg-white p-2 md:p-4 rounded-2xl md:rounded-3xl w-[calc(100dvw-2rem)] max-w-[50rem] 2xl:max-w-[50rem] flex flex-col mx-auto -mt-16 md:-mt-12 lg:-mt-36 xl:-mt-40 gap-4"
					>
						<div>
							<img src={imgTeam} alt="Commarec team" class="rounded-[0.5rem]" />
						</div>

						<div class="p-4 md:p-8">
							<h2 class="font-bold text-cmcsecondary text-[1.25rem]/[1.2] md:text-[2rem]/[1.2] mb-4 md:mb-6">
								<Formatted text="Cítili jsme, že logistika potřebuje změnu. Proto vznikl Commarec" />
							</h2>
							<div class="flex flex-col gap-2 text-2xs/[1.5] md:text-base/[1.5]">
								<p>
									<Formatted
										text="Zajistit, aby logistika bold[[neblokovala růst firem]], ale naopak tvořila pevné základy pro jejich růst."
									/>
								</p>
								<p>
									<Formatted
										text="S takovým cílem jsme s manželkou Markétou zakládali Commarec. Tenkrát nám přišlo, že logistika v Česku potřebuje nový přístup: méně teorie a více praxe."
									/>
								</p>
								<p>
									<Formatted
										text="A tak jsme začali od nuly. Bez investorů, bez kanceláře, jen s notebookem a vizí, že na to půjdeme jinak než ostatní. Později se k nám přidali spolumajitelé Pavel a Jakub."
									/>
								</p>
								<p>
									<Formatted
										text="Na prvních projektech jsme pracovali sami přímo ve skladech. Tehdy nás začal definovat bold[[hands-on přístup]] a bold[[respekt k lidem v provozu]], na těchto pilířích stavíme dodnes."
									/>
								</p>
								<p>
									<Formatted
										text="Pomáháme firmám pochopit, co se v jejich skladech děje, a nastavit bold[[procesy]], bold[[které dávají smysl a přinášejí výsledky]]. To, co navrhneme, umíme i postavit, řídit a předat."
									/>
								</p>
								<p>
									<Formatted
										text="Naši zákazníci jsou firmy, které chtějí mít svou logistiku pod kontrolou, od rostoucích e-shopů po zavedené korporace."
									/>
								</p>
								<p><Formatted text="I vám dokážeme, že logistika není raketová věda." /></p>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div aria-hidden="true" class="xl:h-[15vh]"></div>
		</div>
	</section>

	<!-- Team commarec -->
	<section class="full-width my-8 md:my-16 lg:my-20 mx-3 md:mx-8 xl:-mt-[7.5vh]">
		<div class="2k:mx-auto 2k:w-[116rem]">
			<HugeHeading level={2} text="stroke[[Poznejte]] nl[[]]Tým commarec" class="huge-heading text-cmcsecondary" />
		</div>

		<div
			class="grid gap-2 w-fit mx-auto sm:grid-cols-2 xl:grid-cols-3 -mt-3 md:-mt-5 lg:-mt-6 xl:-mt-11 justify-items-center"
		>
			{#each contacts as member (member.id)}
				<ContactCard {member} isImportant={false} />
			{/each}
		</div>
	</section>

	<!-- Slogan -->
	<section class="full-width my-8 md:my-16 lg:my-20 text-center mx-3 md:mx-8 leading-[1.2]">
		<p class="mb-8 text-[1.25rem]/[1.2] md:text-xl/[1.2] lg:text-[2rem]/[1.2] font-bold text-cmcsecondary">
			<Formatted text="Na čem si zakládáme…" />
		</p>
		<div class="relative inline-block mx-auto">
			<p
				use:hugeHeadingParallax
				class="huge-heading max-w-screen-2xl mb-4 text-cmcsecondary [&_br]:hidden xl:[&_br]:inline"
			>
				<Formatted
					text="q[[stroke[[Nemluví za nás prázdné slogany,]] ale výsledky nl[[]] a důvěra těch, kteří se k nám vracejí.]]"
				/>
			</p>

			<div
				class="mt-6 w-[85%] md:w-auto mx-auto md:mx-0 lg:absolute lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-[calc(50%+1.25rem)]"
			>
				<div class="lg:bg-white/75 lg:backdrop-blur-[8px] p-3 xl:p-6 rounded-[1.5rem]">
					<Link
						icon="arrow"
						href={getCaseStudiesPath(tenant)}
						variant={ButtonVariant.Primary}
						class="flex items-center justify-center font-semibold uppercase tracking-[0.05em] !py-2 md:!py-1 lg:!py-2 rounded-xl text-xs lg:text-[1rem] gap-4 hover:brightness-90"
					>
						<Formatted text="Zobrazit ukázky projektů" />
					</Link>
				</div>
			</div>
		</div>
	</section>
</Layout>
